/**
 * Hero Slider Web Component
 * Handles slide navigation, swipe gestures, and keyboard accessibility
 */
class HeroSlider extends HTMLElement {
  constructor() {
    super();
    this.slides = this.querySelectorAll('.hero-slider__slide');
    this.prevButton = this.querySelector('.hero-slider__arrow--prev');
    this.nextButton = this.querySelector('.hero-slider__arrow--next');
    this.currentIndex = 0;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.isAnimating = false;
    this.autoAdvanceInterval = null;
    this.autoAdvanceDelay = 10000;
    this.resizeTimeout = null;

    this.initFocalPointImages();

    if (this.slides.length > 1) {
      this.bindEvents();
      this.startAutoAdvance();
    }
  }

  /**
   * Initialize focal point positioning for all images
   */
  initFocalPointImages() {
    this.slides.forEach((slide) => {
      const img = slide.querySelector('.hero-slider__masked-image');
      if (!img) return;

      if (img.complete) {
        this.positionImage(img);
      } else {
        img.addEventListener('load', () => this.positionImage(img), { once: true });
      }
    });

    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => this.repositionAllImages(), 150);
    });
  }

  /**
   * Reposition all images (called on resize)
   */
  repositionAllImages() {
    this.slides.forEach((slide) => {
      const img = slide.querySelector('.hero-slider__masked-image');
      if (img && img.complete) {
        this.positionImage(img);
      }
    });
  }

  /**
   * Position an image to center on its focal point while ensuring no edges show
   * @param {HTMLImageElement} img - The image element to position
   */
  positionImage(img) {
    const wrapper = img.closest('.hero-slider__mask-wrapper');
    if (!wrapper) return;

    const focalX = parseFloat(wrapper.dataset.focalX) || 50;
    const focalY = parseFloat(wrapper.dataset.focalY) || 50;

    const containerWidth = wrapper.offsetWidth;
    const containerHeight = wrapper.offsetHeight;

    if (!containerWidth || !containerHeight || !img.naturalWidth || !img.naturalHeight) return;

    const containerAspect = containerWidth / containerHeight;
    const imageAspect = img.naturalWidth / img.naturalHeight;

    /* Calculate base "cover" scale */
    let scale;
    if (imageAspect > containerAspect) {
      /* Image wider than container - scale by height */
      scale = containerHeight / img.naturalHeight;
    } else {
      /* Image taller than container - scale by width */
      scale = containerWidth / img.naturalWidth;
    }

    /* Calculate extra scale based on focal point distance from center */
    /* Centered focal points need minimal extra, edge focal points need more */
    const focalDistanceX = Math.abs(focalX - 50) / 100;
    const focalDistanceY = Math.abs(focalY - 50) / 100;
    const maxFocalDistance = Math.max(focalDistanceX, focalDistanceY);

    /* Min 5% extra (for rotation buffer), max 20% extra (for edge focal points) */
    const minExtraScale = 0.05;
    const maxExtraScale = 0.20;
    const extraScale = minExtraScale + (maxExtraScale - minExtraScale) * (maxFocalDistance * 2);

    scale *= (1 + extraScale);

    const scaledWidth = img.naturalWidth * scale;
    const scaledHeight = img.naturalHeight * scale;

    /* Calculate focal point in pixel terms (scaled) */
    const focalPixelX = (focalX / 100) * scaledWidth;
    const focalPixelY = (focalY / 100) * scaledHeight;

    /* Position so focal point aligns with container center */
    let offsetX = (containerWidth / 2) - focalPixelX;
    let offsetY = (containerHeight / 2) - focalPixelY;

    /* Clamp to ensure no edges show */
    const maxOffsetX = 0;
    const minOffsetX = containerWidth - scaledWidth;
    const maxOffsetY = 0;
    const minOffsetY = containerHeight - scaledHeight;

    offsetX = Math.max(minOffsetX, Math.min(maxOffsetX, offsetX));
    offsetY = Math.max(minOffsetY, Math.min(maxOffsetY, offsetY));

    /* Calculate transform-origin so image rotates around container center, not its own center */
    /* This keeps the image visually static while the mask rotates */
    const originX = (containerWidth / 2) - offsetX;
    const originY = (containerHeight / 2) - offsetY;

    /* Apply calculated dimensions, position, and transform-origin */
    img.style.width = `${scaledWidth}px`;
    img.style.height = `${scaledHeight}px`;
    img.style.left = `${offsetX}px`;
    img.style.top = `${offsetY}px`;
    img.style.transformOrigin = `${originX}px ${originY}px`;
    img.classList.add('is-positioned');
  }

  bindEvents() {
    this.prevButton?.addEventListener('click', () => { this.resetAutoAdvance(); this.prev(); });
    this.nextButton?.addEventListener('click', () => { this.resetAutoAdvance(); this.next(); });

    this.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
    this.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });

    this.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  handleTouchStart(event) {
    this.touchStartX = event.changedTouches[0].screenX;
  }

  handleTouchEnd(event) {
    this.touchEndX = event.changedTouches[0].screenX;
    this.handleSwipe();
  }

  handleSwipe() {
    const swipeThreshold = 50;
    const diff = this.touchStartX - this.touchEndX;

    if (Math.abs(diff) > swipeThreshold) {
      this.resetAutoAdvance();
      if (diff > 0) {
        this.next();
      } else {
        this.prev();
      }
    }
  }

  handleKeydown(event) {
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.resetAutoAdvance();
        this.prev();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.resetAutoAdvance();
        this.next();
        break;
    }
  }

  goToSlide(index) {
    if (index === this.currentIndex || this.isAnimating) return;

    this.isAnimating = true;

    const outgoing = this.slides[this.currentIndex];
    const incoming = this.slides[index];

    // Add exiting class to outgoing slide for scale-down animation
    outgoing.classList.add('is-exiting');
    outgoing.classList.remove('is-active');

    // Activate incoming slide
    incoming.classList.add('is-active');

    this.currentIndex = index;
    this.announceSlideChange();

    // Clean up exiting class after transition completes
    setTimeout(() => {
      outgoing.classList.remove('is-exiting');
      this.isAnimating = false;
    }, 600);
  }

  announceSlideChange() {
    const liveRegion = this.querySelector('.hero-slider__live-region');
    if (liveRegion) {
      liveRegion.textContent = `Slide ${this.currentIndex + 1} of ${this.slides.length}`;
    }
  }

  startAutoAdvance() {
    this.autoAdvanceInterval = setInterval(() => this.next(), this.autoAdvanceDelay);
  }

  resetAutoAdvance() {
    clearInterval(this.autoAdvanceInterval);
    this.startAutoAdvance();
  }

  prev() {
    if (this.isAnimating) return;
    const newIndex = this.currentIndex === 0 ? this.slides.length - 1 : this.currentIndex - 1;
    this.goToSlide(newIndex);
  }

  next() {
    if (this.isAnimating) return;
    const newIndex = this.currentIndex === this.slides.length - 1 ? 0 : this.currentIndex + 1;
    this.goToSlide(newIndex);
  }
}

customElements.define('hero-slider', HeroSlider);
